import { Link } from "react-router-dom"
import {
  Bot,
  Brain,
  Heart,
  Sparkles,
  BookOpen,
  Lightbulb,
  Calendar,
  MessageSquare,
} from "lucide-react"
import type { AskDynamoQuestion } from "~/api/assessmentSessions"
import { AskDynamoPawIllustration } from "~/components/AskDynamoPawIllustration"
import { trackEvent } from "~/utils/trackEvent"
import { substituteDogName } from "~/utils/substituteDogName"

const QUESTION_ICONS = {
  brain: Brain,
  heart: Heart,
  sparkles: Sparkles,
} as const

const BENEFITS = [
  { icon: BookOpen, label: "Full Personality Profile" },
  { icon: Lightbulb, label: "Weekly Perspectives" },
  { icon: Calendar, label: "Personalised Tips" },
  { icon: MessageSquare, label: "Ask Dynamo Guidance" },
] as const

function signupState(sessionToken: string | null, dogName: string) {
  return sessionToken ? { assessmentSessionToken: sessionToken, dogName } : undefined
}

interface MeetAskDynamoSectionProps {
  dogName: string
  exampleQuestions: AskDynamoQuestion[]
  sessionToken: string | null
}

export function MeetAskDynamoSection({
  dogName,
  exampleQuestions,
  sessionToken,
}: MeetAskDynamoSectionProps) {
  const state = signupState(sessionToken, dogName)

  function handlePrimaryCtaClick() {
    trackEvent("ask_dynamo_cta_tap", { dogName })
  }

  return (
    <div className="mt-6 flex flex-col gap-6">
      <section
        className="relative overflow-hidden rounded-2xl border border-[var(--color-ask-dynamo)]/20 bg-gradient-to-br from-[var(--color-ask-dynamo-surface)] to-white/80 p-5 shadow-[0_8px_32px_rgba(108,61,242,0.12)]"
        aria-labelledby="meet-ask-dynamo-heading"
      >
        <span className="inline-flex rounded-full bg-[var(--color-ask-dynamo)] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          New
        </span>

        <div className="mt-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2
              id="meet-ask-dynamo-heading"
              className="flex items-center gap-2 text-lg font-semibold text-[var(--color-ask-dynamo-muted)]"
            >
              <Bot size={20} className="shrink-0" aria-hidden />
              Understand {dogName} better
            </h2>
            <p className="mt-1 text-sm font-medium text-[var(--color-ask-dynamo)]">
              Ask anything about your dog.
            </p>
          </div>
          <AskDynamoPawIllustration className="h-16 w-16 shrink-0" />
        </div>

        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          Get personalised guidance about {dogName}&apos;s behaviour, training, emotions and more —
          tailored to their unique personality.
        </p>

        <ul className="mt-4 flex flex-col gap-2" aria-label="Example questions">
          {exampleQuestions.map((question, index) => {
            const Icon = QUESTION_ICONS[question.icon]
            return (
              <li
                key={index}
                className="flex items-start gap-2.5 rounded-xl border border-white/80 bg-white/70 px-3 py-2.5 text-left text-sm text-[var(--color-text-primary)] shadow-sm"
              >
                <Icon
                  size={16}
                  className="mt-0.5 shrink-0 text-[var(--color-ask-dynamo)]"
                  aria-hidden
                />
                <span>{substituteDogName(question.text, dogName)}</span>
              </li>
            )
          })}
        </ul>

        <Link
          to="/signup"
          state={state}
          onClick={handlePrimaryCtaClick}
          className="mt-4 block w-full rounded-lg bg-[var(--color-ask-dynamo)] px-4 py-3 text-center text-sm font-medium text-white transition hover:opacity-90"
        >
          Start Understanding {dogName}
        </Link>
      </section>

      <section aria-labelledby="unlock-benefits-heading">
        <h3
          id="unlock-benefits-heading"
          className="text-center text-sm font-semibold text-[var(--color-text-primary)]"
        >
          What you&apos;ll unlock with a free account
        </h3>
        <ul className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {BENEFITS.map(({ icon: Icon, label }) => (
            <li key={label} className="flex flex-col items-center gap-2 text-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-surface-card)] text-[var(--color-text-primary)]">
                <Icon size={18} aria-hidden />
              </span>
              <span className="text-xs leading-snug text-[var(--color-text-secondary)]">
                {label}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col items-center gap-3">
        <Link
          to="/signup"
          state={state}
          className="block w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-3 text-center text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90"
        >
          Start Understanding {dogName}
        </Link>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[var(--color-text-primary)] underline">
            Log in
          </Link>
        </p>
      </section>
    </div>
  )
}
